import React, { useEffect, useState } from 'react';
import { useUserProfile } from '../context/UserProfileContext';
import { getStoredUser, resolveUserDisplayName } from '../utils/auth';
import { isAboveSupervisorRole } from '../utils/roles';
import {
  CERT_NOTIFY_OFFSET_MAX,
  CERT_NOTIFY_OFFSET_MIN,
  DEFAULT_CERT_NOTIFY_OFFSET_DAYS,
  describeCertNotifyOffset,
  isSignatureImageDataUrl,
  normalizeCertNotifyOffsetDays,
} from '../utils/userProfile';
import { ProfileSignatureEditor } from './HandwrittenSignatureField';

const inputClass =
  'w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600';

const OFFSET_PRESETS = [-365, -90, -30, 30, 90, 365];

export const CONFIGURE_PROFILE_VIEW = 'configure-profile';

export function ConfigureProfileNavButton({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
        active
          ? 'bg-amber-200/15 border-amber-300/50 text-amber-200'
          : 'bg-gray-900 border-gray-700 text-amber-200/90 hover:bg-gray-800 hover:border-amber-300/40 hover:text-amber-200'
      }`}
    >
      Configure profile
    </button>
  );
}

/** Sidebar heading showing the logged-in user's name. */
export function SidebarUserHeading({ user, className = 'text-xl font-bold' }) {
  const { name: profileName } = useUserProfile();
  const displayName = resolveUserDisplayName({
    profileName,
    projectName: user?.project_name,
    email: user?.email,
  });
  return <h1 className={`${className} truncate`}>{displayName}</h1>;
}

/** Collapsible email / role / configure profile block for all role sidebars. */
export function CollapsibleAccountInfo({
  email,
  roleLabel,
  roleClassName = 'text-sky-200/90',
  emailPrefix = '',
  profileActive = false,
  onConfigureProfile,
}) {
  const [open, setOpen] = useState(false);
  if (!email && !roleLabel) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md text-sm transition-colors border ${
          open
            ? 'bg-gray-800 border-gray-700 text-white'
            : 'bg-gray-900/60 border-gray-800 text-gray-200 hover:bg-gray-900 hover:text-white'
        }`}
      >
        <span className="truncate">Account</span>
        <span
          className={`shrink-0 text-base leading-none text-gray-300 transition-transform ${
            open ? 'rotate-90' : ''
          }`}
          aria-hidden="true"
        >
          ›
        </span>
      </button>
      {open ? (
        <div className="mt-1.5 rounded-md border border-gray-800 bg-gray-950/80 px-3 py-2.5 text-gray-400 text-xs space-y-0.5">
          {email ? (
            <p className="truncate">
              {emailPrefix}
              {email}
            </p>
          ) : null}
          {roleLabel ? (
            <p>
              Role: <span className={roleClassName}>{roleLabel}</span>
            </p>
          ) : null}
          {onConfigureProfile ? (
            <div className="pt-2">
              <ConfigureProfileNavButton active={profileActive} onClick={onConfigureProfile} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CertificateNotifyOffsetControl({ value, onChange, disabled = false }) {
  const offset = normalizeCertNotifyOffsetDays(value);
  const [draft, setDraft] = useState(String(offset));

  useEffect(() => {
    setDraft(String(offset));
  }, [offset]);

  const step = (delta) => {
    onChange(normalizeCertNotifyOffsetDays(offset + delta));
  };

  const commitDraft = () => {
    const trimmed = String(draft || '').trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '+') {
      setDraft(String(offset));
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      setDraft(String(offset));
      return;
    }
    const next = normalizeCertNotifyOffsetDays(parsed);
    onChange(next);
    setDraft(String(next));
  };

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950/80 p-3 space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-300">Certificate expiry notifications</p>
        <p className="mt-1 text-[11px] text-gray-500">
          Minus (−) means notify before expiry. Plus (+) means notify after expiry.
          Default is −30 (30 days before). Range is −365 to +365 days. You can type a value or use + / −.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={disabled || offset <= CERT_NOTIFY_OFFSET_MIN}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-700 bg-black text-lg font-semibold text-white hover:bg-gray-900 disabled:opacity-40"
          aria-label="Decrease notification days"
        >
          −
        </button>
        <div className="min-w-0 flex-1 rounded-lg border border-gray-700 bg-black px-3 py-2 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <input
              type="number"
              inputMode="numeric"
              min={CERT_NOTIFY_OFFSET_MIN}
              max={CERT_NOTIFY_OFFSET_MAX}
              step={1}
              value={draft}
              disabled={disabled}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commitDraft}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              className="w-24 rounded border border-gray-600 bg-gray-950 px-2 py-1 text-center text-lg font-semibold text-white [appearance:textfield] focus:outline-none focus:ring-1 focus:ring-amber-300/40 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              aria-label="Notification offset days"
            />
            <span className="text-sm font-medium text-gray-400">days</span>
          </div>
          <p className="mt-1 text-[11px] text-gray-400">{describeCertNotifyOffset(offset)}</p>
        </div>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={disabled || offset >= CERT_NOTIFY_OFFSET_MAX}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-700 bg-black text-lg font-semibold text-white hover:bg-gray-900 disabled:opacity-40"
          aria-label="Increase notification days"
        >
          +
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {OFFSET_PRESETS.map((preset) => {
          const active = offset === preset;
          const label = preset > 0 ? `+${preset}` : String(preset);
          return (
            <button
              key={preset}
              type="button"
              disabled={disabled}
              onClick={() => onChange(preset)}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40 ${
                active
                  ? 'bg-[#eda448] text-gray-900'
                  : 'border border-gray-700 bg-black text-gray-300 hover:border-gray-500 hover:text-white'
              }`}
            >
              {label} days
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ConfigureProfilePanel({ onBack, backLabel = '← Back to dashboard' }) {
  const {
    name,
    signature,
    certificateNotifyOffsetDays,
    loading,
    saving,
    saveProfile,
  } = useUserProfile();
  const role = getStoredUser()?.role;
  const showCertNotifyTiming = isAboveSupervisorRole(role);
  const [form, setForm] = useState({
    name: '',
    signature: '',
    certificate_notify_offset_days: DEFAULT_CERT_NOTIFY_OFFSET_DAYS,
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      name,
      signature,
      certificate_notify_offset_days: normalizeCertNotifyOffsetDays(certificateNotifyOffsetDays),
    });
  }, [name, signature, certificateNotifyOffsetDays]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedSig = String(form.signature || '').trim();
    if (!trimmedSig) {
      setError('Please type, draw, or upload a signature.');
      return;
    }
    try {
      await saveProfile({ ...form, signature: trimmedSig });
      setSuccess(
        showCertNotifyTiming
          ? 'Profile saved. Name, signature, and certificate notification timing are updated.'
          : 'Profile saved. Your name and signature will appear in safety forms.'
      );
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Could not save profile. Please try again.');
    }
  };

  const signaturePreview = form.signature.trim();
  const previewIsImage = isSignatureImageDataUrl(signaturePreview);

  return (
    <div className="max-w-lg">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-1"
        >
          {backLabel}
        </button>
      )}
      <h2 className="text-xl font-semibold text-white mb-1">Configure Profile</h2>
      <p className="text-gray-400 text-xs mb-6">
        {showCertNotifyTiming
          ? 'Save name, handwritten signature, and when to be notified about certificate expiry.'
          : 'Save name and handwritten signature for auto-fill on forms.'}
      </p>
      {loading && (
        <p className="text-gray-500 text-sm mb-4">Loading your saved profile…</p>
      )}
      {success && (
        <div className="text-green-400 text-sm bg-green-400/10 py-2 px-3 rounded mb-4">{success}</div>
      )}
      {error && (
        <div className="text-red-400 text-sm bg-red-400/10 py-2 px-3 rounded mb-4">{error}</div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="profile-name" className="block text-sm font-medium text-gray-300 mb-2">
            Name
          </label>
          <input
            id="profile-name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            required
            disabled={loading}
            className={inputClass}
            placeholder="Your full name"
          />
        </div>
        <div>
          <span className="block text-sm font-medium text-gray-300 mb-2">
            Handwritten signature
          </span>
          <ProfileSignatureEditor
            value={form.signature}
            disabled={loading || saving}
            inputClassName={inputClass}
            onChange={(next) => setForm((prev) => ({ ...prev, signature: next }))}
          />
          {signaturePreview ? (
            <div className="mt-3 rounded-lg bg-white border border-gray-300 px-4 py-4 min-h-[4rem] flex items-center justify-center">
              {previewIsImage ? (
                <img
                  src={signaturePreview}
                  alt="Signature preview"
                  className="max-h-24 max-w-full object-contain"
                />
              ) : (
                <span className="signature-cursive text-3xl text-gray-900 leading-none">
                  {signaturePreview}
                </span>
              )}
            </div>
          ) : null}
        </div>
        {showCertNotifyTiming ? (
          <CertificateNotifyOffsetControl
            value={form.certificate_notify_offset_days}
            disabled={loading || saving}
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                certificate_notify_offset_days: next,
              }))
            }
          />
        ) : null}
        <button
          type="submit"
          disabled={loading || saving}
          className="w-full py-3 rounded-lg font-semibold text-gray-900 bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
